import {
  getActivityDefinition,
  getCanonicalActivityId,
  getDefaultFloatActivities,
  getDefaultWeekdaySlots,
  getDefaultWeekendFloatActivities,
  getDefaultWeekendSlots,
} from './lifeSimCatalog.js';

// ─── RESOLVE ACTIVITY ─────────────────────────────────
export function resolveAct(a) {
  if (!a) return null;
  if (typeof a === 'string') {
    return getActivityDefinition(a) ?? {
      id: getCanonicalActivityId(a) ?? a,
      name: a,
      emoji: '●',
      cat: 'leisure',
      dur: [20, 40],
      base: 0.2,
      hw: new Array(24).fill(0),
      sat: {},
      cost: {},
    };
  }
  if (a.hw) return a;
  return getActivityDefinition(a.id) ?? a;
}

// ─── NEEDS ────────────────────────────────────────────
export function initNeeds() {
  return { energy: 1, hunger: 1, thirst: 1, social: 1, fun: 1 };
}

export function tickNeeds(needs, actId, deltaMin) {
  const n = { ...needs };
  // Passive decay per minute
  n.energy  = Math.max(0, n.energy  - 0.00015 * deltaMin);
  n.hunger  = Math.max(0, n.hunger  - 0.00020 * deltaMin);
  n.thirst  = Math.max(0, n.thirst  - 0.00025 * deltaMin);
  n.social  = Math.max(0, n.social  - 0.00010 * deltaMin);
  n.fun     = Math.max(0, n.fun     - 0.00008 * deltaMin);

  // Activity-specific recovery
  const act = resolveAct(actId);
  if (act?.sat) {
    Object.entries(act.sat).forEach(([k, v]) => {
      if (k in n) n[k] = Math.min(1, n[k] + v * deltaMin);
    });
  }
  if (act?.cost) {
    Object.entries(act.cost).forEach(([k, v]) => {
      const key = k.replace('-','');
      if (key in n) n[key] = Math.max(0, n[key] + v * deltaMin);
    });
  }

  // Sleep recovers energy hard
  if (actId === 'sleep') n.energy = Math.min(1, n.energy + 0.0015 * deltaMin);
  // Eating recovers hunger/thirst
  if (actId === 'eating') { n.hunger = Math.min(1, n.hunger + 0.020 * deltaMin); n.thirst = Math.min(1, n.thirst + 0.012 * deltaMin); }

  return n;
}

// ─── SLOT SYSTEM ──────────────────────────────────────
export function pickSlot(slots, firedToday, simMin) {
  const hour = Math.floor((simMin % 1440) / 60);
  const candidates = (slots ?? []).filter(s => {
    if (firedToday.has(s.id)) return false;
    const start = s.winStart;
    const end   = s.winEnd > 24 ? s.winEnd - 24 : s.winEnd;
    if (s.winEnd > 24) return hour >= start || hour < end;
    return hour >= start && hour < end;
  });
  if (!candidates.length) return null;
  return candidates.sort((a, b) => b.priority - a.priority)[0];
}

// ─── ACTIVITY POOL ────────────────────────────────────
export function getActPool(char, isWeekend = false) {
  const base = char?.activities?.length
    ? char.activities.map(resolveAct)
    : getDefaultFloatActivities();

  const filtered = base.filter(a => {
    const av = a.availability ?? 'any';
    if (av === 'weekday' && isWeekend) return false;
    if (av === 'weekend' && !isWeekend) return false;
    return true;
  });

  if (isWeekend) {
    const customWE = (char?.weekendActivities ?? []).map(resolveAct);
    const defaultWE = getDefaultWeekendFloatActivities().filter(wa =>
      !filtered.some(a => a.id === wa.id) && !customWE.some(a => a.id === wa.id)
    );
    return [...filtered, ...customWE, ...defaultWE];
  }
  return filtered;
}

// ─── SCORER ───────────────────────────────────────────
export function scoreFloat(act, char, hour, needs, lastActId, isWeekend = false) {
  const resolved = resolveAct(act);
  if (!resolved) return 0;
  if (resolved.id === lastActId) return 0.015;

  const hval = resolved.hw?.[hour] ?? 0;
  let s = (resolved.base ?? 0.3) * (hval + 0.5);
  const p = char.preferences ?? {};

  if (resolved.cat === 'social')     s *= 0.3 + (p.socialEnergy ?? 0.5);
  if (resolved.cat === 'productive') s *= 0.3 + (p.workEthic    ?? 0.5);
  if (resolved.cat === 'active')     s *= 0.3 + (p.exercise     ?? 0.5);

  if (isWeekend) {
    if (resolved.cat === 'productive')       s *= 0.15;
    if (resolved.cat === 'leisure')          s *= 1.6;
    if (resolved.cat === 'social')           s *= 1.5;
    if (resolved.cat === 'active')           s *= 1.3;
    if (resolved.availability === 'weekend') s *= 2.2;
  } else {
    if (resolved.cat === 'productive' && hour >= 9 && hour < 18) s *= 1.2;
  }

  const nb = (id, cM, wM, cT, wT) => {
    const v = needs[id] ?? 1;
    return v < cT ? cM : v < wT ? wM : 1;
  };
  if (resolved.cat === 'social')  s *= nb('social', 4, 2, 0.15, 0.30);
  if (resolved.cat === 'leisure') s *= nb('fun',    2.5, 1.5, 0.15, 0.32);

  if (resolved.id === 'nap' || resolved.id === 'sleep_in') {
    const isRestWindow = resolved.id === 'sleep_in'
      ? (hour >= 7 && hour < 11)
      : (hour >= 12 && hour < 17);
    if (!isRestWindow) return 0.005;
    const e = needs.energy ?? 1;
    if (e > 0.65 || e < 0.15) return 0.005;
    s = 0.30 * (0.65 - e) / 0.50;
  }

  if (char.traits?.some(t => ['nocturnal','night owl','night-owl'].includes(t.toLowerCase()))) {
    if (hour >= 20 || hour < 6) s *= 1.7;
    if (hour >= 10 && hour < 16) s *= 0.25;
  }

  return Math.max(0.01, s);
}

// ─── WEIGHTED RANDOM PICK ─────────────────────────────
export function weightedPick(items, scoreFn) {
  const scored = items.map(a => ({ a, s: scoreFn(a) }));
  const total  = scored.reduce((sum, x) => sum + x.s, 0);
  if (total <= 0) return items[0];
  let r = Math.random() * total;
  for (const { a, s } of scored) { r -= s; if (r <= 0) return a; }
  return items[items.length - 1];
}

// ─── DOW HELPERS ──────────────────────────────────────
const START_DOW = 1; // sim starts on Monday
export function simDow(dayN)       { return (dayN + START_DOW) % 7; }
export function isWeekendDow(dow)  { return dow === 0 || dow === 6; }

export function getDefaultSlots(isWeekend = false) {
  return isWeekend ? getDefaultWeekendSlots() : getDefaultWeekdaySlots();
}
